<script setup lang="tsx">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps<{ modelValue: File | undefined }>()
const emit = defineEmits<{ 'update:modelValue': [file: File | undefined] }>()

const { t } = useI18n()
const isDragOver = ref(false)
const fileError = ref('')

function onDragOver(e: DragEvent) {
	e.preventDefault()
	isDragOver.value = true
}
function onDragLeave() {
	isDragOver.value = false
}
function onDrop(e: DragEvent) {
	e.preventDefault()
	isDragOver.value = false
	const files = e.dataTransfer?.files
	if (files?.length) setFile(files[0])
}
function onFileChange(e: Event) {
	const input = e.target as HTMLInputElement
	if (input.files?.length) setFile(input.files[0])
}
function setFile(file: File) {
	const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
	if (!['csv', 'xlsx'].includes(ext)) {
		fileError.value = t('misc.import') + ': .csv / .xlsx only'
		emit('update:modelValue', undefined)
		return
	}
	if (file.size > 10 * 1024 * 1024) {
		fileError.value = t('misc.import') + ': max 10 MB'
		emit('update:modelValue', undefined)
		return
	}
	fileError.value = ''
	emit('update:modelValue', file)
}
</script>

<template>
	<div
		class="drop-zone border rounded p-5 text-center"
		:class="{ 'border-primary bg-primary-subtle': isDragOver }"
		@dragover="onDragOver"
		@dragleave="onDragLeave"
		@drop="onDrop"
	>
		<i class="bi bi-cloud-upload fs-1 d-block mb-2 text-secondary"></i>
		<p class="mb-1">{{ t('misc.import') }} .csv / .xlsx</p>
		<p class="text-muted small mb-3">{{ t('misc.loading') }}… max 10 MB</p>
		<label class="btn btn-outline-primary btn-sm">
			{{ t('misc.select') }}
			<input type="file" accept=".csv,.xlsx" class="d-none" data-test="import-file-input" @change="onFileChange" />
		</label>
	</div>

	<div v-if="modelValue" class="mt-3 d-flex align-items-center gap-2">
		<i class="bi bi-file-earmark-check text-success fs-5"></i>
		<span>{{ modelValue.name }}</span>
		<button class="btn btn-link btn-sm text-danger p-0" @click="emit('update:modelValue', undefined)">
			<i class="bi bi-x-lg"></i>
		</button>
	</div>
	<div v-if="fileError" class="alert alert-danger mt-2 py-1">{{ fileError }}</div>
</template>

<style lang="sass" scoped>
.drop-zone
	border-style: dashed !important
	cursor: pointer
	transition: background-color 0.2s, border-color 0.2s
</style>
